import { css } from '@emotion/react'
import styled from '@emotion/styled'
import {
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
} from 'react'
import { sendPrivateMessage } from '../../../../actions/sendPrivateMessage'
import { getScreenState } from '../../../../app-state/state.screen'
import { VideoType } from '../../../../constants/enums'
import { FloaterSize, QUICK_REPLY } from '../../../../constants/floaters'
import {
    FloaterState,
    QUICK_REPLY_CHAT_HEIGHT,
    QUICK_REPLY_HEIGHT,
    QUICK_REPLY_MOBILE_CHAT_HEIGHT,
    QUICK_REPLY_MOBILE_HEIGHT,
    QUICK_REPLY_MOBILE_WIDTH,
    QUICK_REPLY_WIDTH,
    RoomView,
} from '../../../../state/modals'
import {
    closeFloater,
    toggleQuickReplySize,
} from '../../../../state/modals/state.floater'
import { idToSlug } from '../../../../state/state.users'
import { isTouchDevice } from '../../../../utilities/browser'
import i18n from '../../../../utilities/i18n'
import { AppStateType, useAppState } from '../../../Hooks/useAppState'
import { useFloatingRoom } from '../../../Hooks/useModal'
import { usePMSelector } from '../../../Hooks/usePM'
import { useUserSelector } from '../../../Hooks/useUser'
import BadgeAvatar from '../../../common/BadgeAvatar'
import { palette } from '../../../styles/colors'
import layers from '../../../styles/layers'
import shape from '../../../styles/shape'
import { CHAT_HEAD_WIDTH } from '../../../theme'
import OnlineChatHeader from '../../Chat/ChatHeader/OnlineChatHeader'
import Compose from '../../Chat/Compose'
import BackgroundFade from '../../Chat/Rooms/BackgroundFade'
import Chat from '../../Chat/Rooms/Chat'
import LiveSnapStream from '../../LiveSnapStream'
import Notification from '../../Notification'
import {
    FloaterModalComponentProps,
    checkParent,
    clickParent,
} from '../Floater'

const quickReplyStyles = css({
    borderRadius: shape.borderRadius,
    background: palette.background,
    height: QUICK_REPLY_HEIGHT,
    width: QUICK_REPLY_WIDTH,
    flexDirection: 'column',
    overflow: 'hidden',
    display: 'flex',
    '& > div > div': {
        borderRadius: 0,
    },
})
const largeQuickReplyStyles = css({
    height: 48 + 41 + QUICK_REPLY_WIDTH * (9 / 16) * 2,
})

const mobileQuickReplyStyles = css({
    width: QUICK_REPLY_MOBILE_WIDTH,
    height: QUICK_REPLY_MOBILE_HEIGHT,
    '& input': {
        width: '100%',
    },
})

const composeContainerStyles = css({
    borderTop: `1px solid ${palette.drawerDivider}`,
})
const headerShadowStyles = css({
    borderBottom: `1px solid ${palette.drawerDivider}`,
})

const SnapStreamContainer = styled('div')({
    position: 'relative',
    height: QUICK_REPLY_WIDTH * (9 / 16),
    boxShadow: layers.one,
})

const ChatHead = styled('div')({
    position: 'relative',
    boxShadow: `0px 0px 4px ${palette.contrastIcons}, ` + layers.one,
    borderRadius: '50%',
    '& > div': {
        position: 'absolute',
        right: 0,
        bottom: 0,
        zIndex: 999,
    },
})

export enum QuickReplyActions {
    chat,
    input,
    header,
    video,
}

const QuickReply = memo((props: FloaterState & FloaterModalComponentProps) => {
    const replyContainerRef = useRef<HTMLDivElement>(null)
    const chatRef = useRef(null)
    const inputContainer = useRef(null)
    const headerContainer = useRef(null)
    const videoContainerRef = useRef(null)
    const allMessages = usePMSelector('chats')

    const { isMobile, isIOS, darkMode } = useAppState(AppStateType.APP)
    const { id, minimized, vertical, moveFloater, size, eventsRef } = props

    const banned = useUserSelector(id, 'banned')
    const isOffline = useUserSelector(id, 'isOffline')
    const name = useUserSelector(id, 'name')
    const isTruePrivate = useUserSelector(id, 'isTruePrivate')
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isPrivateShow = useUserSelector(id, 'isPrivateShow')
    const isGroupShow = useUserSelector(id, 'isGroupShow')
    const isNGVideo = useUserSelector(id, 'isNGVideo')
    const isClubShow = useUserSelector(id, 'isClubShow')
    const channel = useUserSelector(id, 'channel')
    const server = useUserSelector(id, 'server')
    const isLounge = useUserSelector(id, 'isLounge')
    const isModel = useUserSelector(id, 'isModel')
    const phase = useUserSelector(id, 'phase')
    const isAway = useUserSelector(id, 'isAway')
    const slug = useUserSelector(id, 'slug')

    // const floatingPortalId = useFloatingRoomId()

    const [floatingRoom] = useFloatingRoom()
    const floatingPortalId = floatingRoom.show ? floatingRoom.state.id : null
    const isFloatingRoomFullscreen =
        floatingRoom.state.size === FloaterSize.Fullscreen
    const isPmPage =
        floatingRoom.state.roomView === RoomView.PM && floatingPortalId === id
    const isCurrentRoomPage =
        floatingRoom.state.roomView === RoomView.Room && floatingPortalId === id
    const isCurrentProfilePage =
        floatingRoom.state.roomView === RoomView.Profile &&
        floatingPortalId === id

    const videoWidth = QUICK_REPLY_WIDTH
    const videoHeight = videoWidth * (9 / 16)
    const isLarge = size > FloaterSize.Medium
    const shouldShowVideo = isLarge && !banned && !isOffline
    const showVideoToggle =
        !isCurrentProfilePage && !isCurrentRoomPage && !isMobile && !isPmPage

    useEffect(() => {
        if (!id || banned) closeFloater(id, QUICK_REPLY)
    }, [id, banned])
    useImperativeHandle(eventsRef, () => {
        return {
            abandonDrag: (event) => {
                const target = event.target as HTMLElement
                if (checkParent(target, chatRef.current, 8) && isTouchDevice)
                    return true

                return false
            },
            allowDefault: (event) => {
                const target = event.target as HTMLElement

                if (checkParent(target, chatRef.current, 8) && isTouchDevice)
                    return true
                return false
            },
            onAction: (event, start, _meta, data) => {
                if (start) return
                const target = event.target as HTMLElement
                const chatInput = inputContainer.current?.querySelector('input')
                switch (data) {
                    case QuickReplyActions.input:
                        if (isMobile && vertical && !isIOS) {
                            // reposition the floater to a safe spot above the virtual keyboard
                            const quickReplyWidth = isMobile
                                ? QUICK_REPLY_MOBILE_WIDTH
                                : QUICK_REPLY_WIDTH
                            const { windowWidth } = getScreenState()
                            moveFloater((windowWidth - quickReplyWidth) / 2, 20)
                        }
                        if (chatInput !== target) {
                            clickParent(target, 4)
                        } else {
                            chatInput?.focus()
                        }
                        break
                    case QuickReplyActions.video:
                        break
                    case QuickReplyActions.header:
                        if (isTouchDevice) {
                            clickParent(target, 8)
                            chatInput?.blur()
                        }
                        break
                    case QuickReplyActions.chat:
                    default:
                        clickParent(target, 4)
                        chatInput?.blur()
                        break
                }
            },
            onCheck: (event, start, _metadata) => {
                const target = event.target as HTMLElement
                if (!start) {
                    if (checkParent(target, chatRef.current, 8))
                        return [true, QuickReplyActions.chat, false]
                    if (checkParent(target, videoContainerRef.current, 8))
                        return [true, QuickReplyActions.video, false]
                    if (checkParent(target, inputContainer.current, 4))
                        return [true, QuickReplyActions.input, false]
                    if (checkParent(target, headerContainer.current, 8))
                        return [true, QuickReplyActions.header, false]
                }

                if (checkParent(target, chatRef.current, 8))
                    return [false, QuickReplyActions.chat, false]
                return [false, null, false]
            },
        }
    }, [id])

    useEffect(() => {
        // in a few scenarios we need to shrink the quick reply
        if ((isCurrentRoomPage || isCurrentProfilePage) && shouldShowVideo) {
            toggleQuickReplySize(id)
        }
    }, [isCurrentRoomPage, isCurrentProfilePage, shouldShowVideo])

    const onSend = useCallback((id: number, text: string) => {
        sendPrivateMessage(id, text)
    }, [])
    if (!id || banned) return null

    if (vertical && minimized && !isPmPage) {
        const messages = allMessages[idToSlug[id]]
        return (
            <ChatHead
                ref={replyContainerRef}
                data-testid="Container__ModelAvatar"
            >
                <BadgeAvatar
                    offline={isOffline}
                    alt={name}
                    size={CHAT_HEAD_WIDTH}
                    id={id}
                />
                {messages?.unread > 0 && (
                    <Notification value={messages?.unread} />
                )}
            </ChatHead>
        )
    } else if (minimized || (isPmPage && isFloatingRoomFullscreen)) return null

    return (
        <div
            css={[
                quickReplyStyles,
                isMobile && mobileQuickReplyStyles,
                shouldShowVideo && largeQuickReplyStyles,
            ]}
            ref={replyContainerRef}
        >
            <div
                css={!shouldShowVideo && headerShadowStyles}
                ref={headerContainer}
            >
                <OnlineChatHeader
                    id={id}
                    pinnedPm
                    toggleVideo={showVideoToggle}
                    videoToggled={shouldShowVideo}
                />
            </div>
            {shouldShowVideo && (
                <BackgroundFade
                    id={id}
                    width={videoWidth}
                    height={videoHeight}
                    top={20}
                    filter={
                        darkMode
                            ? 'blur(24px) saturate(130%) opacity(50%) brightness(95%)'
                            : 'blur(24px) saturate(130%) opacity(40%) contrast(155%) brightness(95%)'
                    }
                />
            )}

            {shouldShowVideo && (
                <SnapStreamContainer ref={videoContainerRef}>
                    <LiveSnapStream
                        videoMuted
                        volumeOnly
                        id={id}
                        width={videoWidth}
                        height={videoHeight}
                        isTruePrivate={isTruePrivate}
                        isBroadcasting={isBroadcasting}
                        isPrivateShow={isPrivateShow}
                        isNGVideo={isNGVideo}
                        isGroupShow={isGroupShow}
                        isClubShow={isClubShow}
                        isOffline={isOffline}
                        channel={channel}
                        banned={banned}
                        server={server}
                        isLounge={isLounge}
                        isModel={isModel}
                        phase={phase}
                        isAway={isAway}
                        videoID={VideoType.PinnedPm}
                        key={`pinned-pm-snap-stream-${slug}`}
                    />
                </SnapStreamContainer>
            )}
            <Chat
                chatRef={chatRef}
                styleHeight={`${
                    shouldShowVideo
                        ? videoHeight
                        : isMobile
                          ? QUICK_REPLY_MOBILE_CHAT_HEIGHT
                          : QUICK_REPLY_CHAT_HEIGHT
                }px`}
                isQuickReply
                id={id}
                noNews
                pm
            />
            <div css={composeContainerStyles} ref={inputContainer}>
                <Compose
                    id={id}
                    onSend={onSend}
                    placeholder={i18n.t('chat.sendPrivateMessage')}
                    // vertical
                    pinnedPm
                    noFullWindowButtons
                />
            </div>
        </div>
    )
})

export default QuickReply
